import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, Save, ChevronUp, ChevronDown, Languages, Loader2, Download, Upload } from 'lucide-react';
import { adminAPI } from '../../services/api';
import '../../styles/AdminGlobal.css';
import '../../styles/AdminShared.css';
import './Admin.css';

const TranslationManagement: React.FC = () => {
  const [translations, setTranslations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('key');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [edits, setEdits] = useState<Record<number, { text_en: string; text_bn: string }>>({});
  const [saving, setSaving] = useState<Record<number, boolean>>({});

  useEffect(() => {
    loadTranslations();
  }, []);

  const loadTranslations = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getTranslations();
      const data = response.data || response;
      const translationsList = Array.isArray(data) ? data : (data as any).results || [];
      setTranslations(translationsList);
    } catch (error) {
      console.error('Failed to load translations:', error);
      setTranslations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (row: any) => {
    const rowEdit = edits[row.id];
    const payload = rowEdit
      ? { text_en: rowEdit.text_en, text_bn: rowEdit.text_bn }
      : { text_en: row.text_en, text_bn: row.text_bn };
    setSaving(s => ({ ...s, [row.id]: true }));
    try {
      await adminAPI.updateTranslation(row.id, payload);
      setTranslations(prev => prev.map(t => t.id === row.id ? { ...t, ...payload } : t));
      setEdits(e => { const next = { ...e }; delete next[row.id]; return next; });
    } catch (error) {
      console.error('Failed to update translation:', error);
      alert('Failed to update translation.');
    } finally {
      setSaving(s => ({ ...s, [row.id]: false }));
    }
  };

  const handleSort = (f: string) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
    setCurrentPage(1);
  };
  const SA = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={10} className="adm-sort-arrow" /> : <ChevronDown size={10} className="adm-sort-arrow" />;
  };

  const translationsArray = Array.isArray(translations) ? translations : [];
  const filtered = useMemo(() => {
    let list = translationsArray;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(t =>
        t.key?.toLowerCase().includes(q) ||
        t.text_en?.toLowerCase().includes(q) ||
        t.text_bn?.toLowerCase().includes(q) ||
        t.category?.toLowerCase().includes(q)
      );
    }
    list = [...list].sort((a: any, b: any) => {
      const av = a[sortField] ?? ''; const bv = b[sortField] ?? '';
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return list;
  }, [translationsArray, searchTerm, sortField, sortDir]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pageNums = (() => {
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); return pages; }
    pages.push(1); if (currentPage > 3) pages.push('...');
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('...'); pages.push(totalPages); return pages;
  })();

  return (
    <div className="admin-page adm-page">
      <div className="adm-topbar">
        <h1 className="adm-page-title">Translations</h1>
        <div className="adm-topbar-right">
          <button className="adm-btn adm-btn-export"><Download size={13} /> Export</button>
          <button className="adm-btn adm-btn-purple"><Upload size={13} /> Import</button>
          <button className="adm-btn adm-btn-refresh" onClick={loadTranslations} disabled={loading}>
            {loading ? <span className="adm-spin"><Loader2 size={13} /></span> : <RefreshCw size={13} />} Refresh
          </button>
        </div>
      </div>
      <div className="adm-filter-wrap">
        <div className="adm-filter-left">
          <span className="adm-count"><strong>{filtered.length}</strong> translations</span>
        </div>
        <div className="adm-filter-right">
          <div className="adm-search-wrap">
            <span className="adm-search-icon"><Search size={13} /></span>
            <input className="adm-search-input" placeholder="Search by key, category or text…" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
        </div>
      </div>
      <div className="adm-table-card">
        <div className="adm-table-scroll">
          <table className="adm-table">
            <thead>
              <tr>
                <th className="adm-th adm-th-sort" style={{ minWidth: 220 }} onClick={() => handleSort('key')}>Key <SA field="key" /></th>
                <th className="adm-th adm-th-sort" style={{ width: 110 }} onClick={() => handleSort('category')}>Category <SA field="category" /></th>
                <th className="adm-th" style={{ minWidth: 200 }}>English</th>
                <th className="adm-th" style={{ minWidth: 200 }}>Bangla</th>
                <th className="adm-th" style={{ width: 70, textAlign: 'center' }}>Save</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="adm-loading"><span className="adm-spin" style={{ marginRight: 8 }}><Loader2 size={18} /></span>Loading…</td></tr>}
              {!loading && paginated.length === 0 && <tr><td colSpan={5} className="adm-empty">No translations found.</td></tr>}
              {!loading && paginated.map((t: any) => {
                const edit = edits[t.id];
                const enVal = edit ? edit.text_en : (t.text_en || '');
                const bnVal = edit ? edit.text_bn : (t.text_bn || '');
                const isDirty = !!edit;
                return (
                  <tr className="adm-row" key={t.id}>
                    <td className="adm-td"><span className="adm-mono" style={{ fontSize: 11 }}>{t.key}</span></td>
                    <td className="adm-td"><span className="adm-chip adm-chip-blue" style={{ fontSize: 10 }}>{t.category || '—'}</span></td>
                    <td className="adm-td" style={{ padding: '4px 8px' }}>
                      <input
                        className="adm-input"
                        style={{ fontSize: 12, padding: '4px 8px', minWidth: 180 }}
                        value={enVal}
                        onChange={e => setEdits(ed => ({ ...ed, [t.id]: { text_en: e.target.value, text_bn: edit?.text_bn ?? (t.text_bn || '') } }))}
                      />
                    </td>
                    <td className="adm-td" style={{ padding: '4px 8px' }}>
                      <input
                        className="adm-input"
                        style={{ fontSize: 12, padding: '4px 8px', minWidth: 180 }}
                        value={bnVal}
                        onChange={e => setEdits(ed => ({ ...ed, [t.id]: { text_en: edit?.text_en ?? (t.text_en || ''), text_bn: e.target.value } }))}
                      />
                    </td>
                    <td className="adm-td" style={{ textAlign: 'center', padding: '4px' }}>
                      <button
                        className={`adm-action-btn ${isDirty ? 'adm-ab-approve' : 'adm-ab-edit'}`}
                        title="Save"
                        disabled={saving[t.id]}
                        onClick={() => handleSave(t)}
                      >
                        {saving[t.id] ? <span className="adm-spin"><Loader2 size={11} /></span> : <Save size={11} />}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="adm-pagination">
          <div className="adm-page-size-wrap"><span>Show</span><select className="adm-page-size-select" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>{[10,20,50,100].map(n => <option key={n} value={n}>{n}</option>)}</select><span>per page</span></div>
          <div className="adm-page-info">{filtered.length === 0 ? '0 results' : `${(currentPage-1)*pageSize+1}–${Math.min(currentPage*pageSize,filtered.length)} of ${filtered.length}`}</div>
          <div className="adm-page-controls">
            <button className="adm-page-btn" disabled={currentPage===1} onClick={() => setCurrentPage(p => p-1)}>‹</button>
            <div className="adm-page-numbers">{pageNums.map((n,i) => n==='...' ? <span key={`e${i}`} style={{padding:'0 4px',color:'#94a3b8'}}>…</span> : <button key={n} className={`adm-page-num${currentPage===n?' active':''}`} onClick={() => setCurrentPage(n as number)}>{n}</button>)}</div>
            <button className="adm-page-btn" disabled={currentPage===totalPages} onClick={() => setCurrentPage(p => p+1)}>›</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TranslationManagement;
